<template>
  <v-dialog
    v-model="showImagesPreviewDialog"
    max-width="880"
    :transition="false"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card
      class="v-card"
    >
      <v-card-title
        class="v-card-title"
      >
        {{ currentDialogTitle }}
        <v-spacer />

        <div
          class="x-image"
        >
          <image-field
            src="/img/icons/x-icon-image.svg"
            contain
            width="30"
            height="30"
            @onClick="closeDialog"
          />
        </div>
      </v-card-title>
      <v-card-text
        class="v-card-text"
      >
        <!--    Loading spinner button-->
        <v-overlay
          v-if="showLoader"
          opacity="0.2"
          absolute
          color="#c8c9cb"
        >
          <v-progress-circular
            indeterminate
            :color="'var(--v-primary-base)'"
            size="60"
          />
        </v-overlay>
        <template
          v-if="existingImages && existingImages.length"
        >
          <v-row>
            <v-col>
              <v-carousel
                v-model="selectedCarouselItem"
                :show-arrows="!(!existingImages || !existingImages.length || existingImages.length === 1)"
                hide-delimiters
                hide-delimiter-background
                class="v-carousel"
                @change="setImageForDownload($event)"
              >
                <v-carousel-item
                  v-for="(image, imageIndex) in existingImages"
                  :key="'image_' + imageIndex"
                >
                  <v-img
                    :src="getImageUrl(image)"
                    contain
                    class="image"
                  />
                </v-carousel-item>
              </v-carousel>
            </v-col>
          </v-row>

          <v-row
            justify="center"
            class="d-flex flex-wrap"
          >
            <div
              v-for="(image, imageIndex) in existingImages"
              :key="'images_list_' + imageIndex"
              class="image-wrapper"
              @click="setImageForDownload(imageIndex)"
            >
              <v-img
                :src="getImageUrl(image)"
                contain
                class="image"
                :class="currentImageIndex === imageIndex ? 'selected-image-in-list' : 'normal-image-in-list'"
              />
              <div
                :class="currentImageIndex === imageIndex ? 'image-mask' : ''"
              />
            </div>
          </v-row>
        </template>
        <!--        no data text-->
        <template
          v-else
        >
          <div
            style="text-align: center;"
          >
            {{ $t('base.no_data') }}
          </div>
        </template>
      </v-card-text>

      <!--      Action buttons-->
      <v-card-actions
        v-if="enableActions"
        class="v-card-actions"
      >
        <v-spacer />

        <universal-button
          type="base"
          color="#111827"
          style="color: white; width: 130px; height: 40px;"
          :label="$t('base.close')"
          @click="closeDialog"
        />

        <universal-button
          v-if="imageForDownload && enableDelete"
          type="base"
          color="#EF4444"
          :label="$t('base.delete')"
          style="color: white; width: 130px; height: 40px;"
          @click="removeImage"
        />

        <universal-button
          v-if="imageForDownload"
          type="base"
          style="color: white; width: 130px; height: 40px;"
          :label="$t('base.download')"
          color="#65AD45"
          @click="downloadImage"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { isEmpty } from 'lodash'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import ImageField from '@/global/components/fields/ImageField.vue'

export default {
  name: 'ImagesPreviewDialog',

  components: {
    ImageField,
    UniversalButton
  },

  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    images: {
      type: Array,
      default: () => ([])
    },
    showLoader: {
      type: Boolean,
      default: false
    },
    tabKey: {
      type: String,
      default: ''
    },
    fieldKey: {
      type: String,
      default: ''
    },
    field: {
      type: Object,
      default: () => ({})
    },
    dialogTitle: {
      type: String,
      default: ''
    },
    // Which image the dialog opens on, for a caller that opened it by clicking one of
    // them. Left null the dialog behaves as it always did: the carousel starts on the
    // first image and nothing counts as picked until the user picks it, so there is no
    // download button until then.
    initialImageIndex: {
      type: Number,
      default: null
    },
    enableActions: {
      type: Boolean,
      default: true
    },
    // Read-only callers (preview + download, no removing) turn this off; every
    // existing caller keeps the delete button it already had.
    enableDelete: {
      type: Boolean,
      default: true
    },
    removeCallback: {
      type: Function,
      default: () => {}
    },
    removeApiConfig: {
      type: Object,
      default: () => ({})
    }
  },

  emits: [
    'close',
    'removed-image'
  ],

  data () {
    return {
      imageForDownload: null,
      currentImageIndex: null,
      selectedCarouselItem: null
    }
  },

  computed: {
    showImagesPreviewDialog: {
      get () {
        return this.showDialog
      },
      set (val) {
        return val
      }
    },

    existingImages: {
      get () {
        return this.images?.map(image => {
          return image?.data || image
        })
      },
      set (val) {
        return val
      }
    },

    // An image can name itself through its own `title` - the header then follows
    // whichever one is on screen instead of labelling the whole set. Callers that
    // don't set it keep the single `dialogTitle` they always had.
    // The index is null until the carousel is first navigated (or a thumbnail
    // clicked), so it falls back to the first image - which is what the carousel
    // shows on open.
    currentDialogTitle () {
      const currentImage = this.existingImages?.[this.currentImageIndex ?? 0]
      return currentImage?.title || this.dialogTitle
    }
  },

  watch: {
    showDialog: {
      immediate: true,
      handler (isOpen) {
        if (!isOpen || this.initialImageIndex === null) {
          return
        }

        this.setImageForDownload(this.initialImageIndex)
      }
    },

    existingImages: {
      immediate: true,
      deep: true,
      handler (images) {
        if (!images || !images.length) {
          this.imageForDownload = null
          this.currentImageIndex = null
        }
      }
    }
  },

  methods: {
    isEmpty,

    getImageUrl (image) {
      return image?.original_url || image?.url || image
    },

    setImageForDownload (imageIndex) {
      this.currentImageIndex = imageIndex
      this.selectedCarouselItem = imageIndex
      if (this.existingImages?.[imageIndex]) {
        this.imageForDownload = this.getImageUrl(this.existingImages[imageIndex])
      }
    },

    downloadImage () {
      // Create a temporary anchor element
      const link = document.createElement('a')
      link.href = this.imageForDownload
      link.download = this.imageForDownload?.name || 'image'
      link.click()
    },

    closeDialog () {
      this.$emit('close')
    },

    async removeImage () {
      if (this.currentImageIndex !== null) {
        const imageToRemove = this.images[this.currentImageIndex]
        if (typeof this.removeCallback === 'function' && imageToRemove) {
          this.removeCallback({
            removeApiConfig: this.removeApiConfig,
            file: imageToRemove,
            index: this.currentImageIndex,
            tabKey: this.tabKey,
            fieldKey: this.fieldKey,
            field: this.field
          })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-window__next > .v-btn {
    height: 35px;
    width: 35px;
  }

  .v-window__next > .v-btn > .v-btn__content > .v-icon {
    font-size: 2rem !important;
  }

  .v-window__next {
    background: #1F2937 !important;
  }

  .v-window__prev> .v-btn > .v-btn__content > .v-icon {
    font-size: 2rem !important;
  }

  .v-window__prev > .v-btn {
    height: 35px;
    width: 35px;
  }

  .v-window__prev {
    background: #1F2937 !important;
  }
}

.v-card-text {
  background-color: #F9FAFB;
  padding: 24px 40px 40px 40px;
}

.v-card-title {
  font-family: 'Satoshi', sans-serif;
  font-size: 18px !important;
  font-weight: 700 !important;
  letter-spacing: 0;
  border-bottom: 1px solid #E5E7EB;
}

.v-card-actions {
  font-size: 20px;
  border-top: 1px solid #E5E7EB;
}

.v-card {
  max-width: 880px;
  border-radius: 16px;
}

.image {
  max-height: 100%;
  max-width: 100%;
  border-radius: 12px !important;
}

.normal-image-in-list {
  height: 72px;
  width: 72px;
  margin-right: 8px;
  border-radius: 12px;
}

.selected-image-in-list {
  height: 100px;
  width: 100px;
  margin-right: 8px;
  margin-top: 5px;
  margin-left: 2px;
}

.image-wrapper {
  position: relative;
  margin: 8px;
  display: flex;
  max-width: 100px;
  max-height: 100px;
  cursor: pointer;
  padding: 5px;
}

.image-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 103px;
  height: 110px;
  border-radius: 12px;
  background-color: #3B82F6; /* Your desired background color */
  opacity: 0.5; /* Adjust opacity for transparency */
  z-index: 1; /* Ensure the mask is layered on top of the image */
}

.x-image:hover {
  cursor: pointer;
}
</style>
